import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import type { Edge } from '../api/types.js';
import { useApi, useAuth } from '../auth.js';
import { canOperateSite, isPlatform } from '../lib/permissions.js';
import { Age, ErrorBox, Spinner, StatusBadge } from '../components.js';
import { useNow, useResource } from '../hooks.js';
import {
  formatCount,
  formatDuration,
  formatFloat,
  formatMegabytes,
  formatTimestamp,
  PLACEHOLDER,
} from '../lib/format.js';
import { edgeHealth } from '../lib/health.js';
import {
  AddEdgeForm,
  EdgeLifecycleActions,
  EnrollmentCell,
  InstallPanel,
  LifecycleResultPanel,
  VpnCell,
  VpnKeyPanel,
  type EdgePanel,
} from '../EdgeLifecycle.js';

export const EDGE_REFRESH_MS = 10_000;

function n(v: number | null, f: (v: number) => string = formatCount): string {
  return v === null ? PLACEHOLDER : f(v);
}

/** Router state for the site page: a panel to show once (after "Add edge" on the sites page). */
export interface SitePageState {
  panel?: EdgePanel;
}

function EdgeRow({
  edge,
  siteId,
  now,
  timeZone,
  showVpn,
  platform,
  onPanel,
  onChanged,
}: {
  edge: Edge;
  siteId: string;
  now: number;
  timeZone: string;
  showVpn: boolean;
  /** Platform staff: infrastructure links and enrollment (ADR 0010). */
  platform: boolean;
  onPanel: (p: EdgePanel) => void;
  onChanged: () => void;
}) {
  const h = edgeHealth(edge);
  const devicesWarn =
    h.bacnetDevices !== null &&
    h.bacnetDevicesOnline !== null &&
    h.bacnetDevicesOnline < h.bacnetDevices;
  return (
    <tr className={edge.disabled_at != null ? 'row-disabled' : undefined}>
      <td>
        <div className="strong mono">{edge.edge_node_id}</div>
        {edge.name && <div className="small">{edge.name}</div>}
        <div className="muted small">
          {formatCount(edge.devices)} devices · {formatCount(edge.points)} points
        </div>
        <div className="row-actions">
          <Link
            className="btn btn-small"
            to={`/points?site_id=${encodeURIComponent(siteId)}&edge_id=${encodeURIComponent(edge.id)}`}
          >
            Points
          </Link>
          {platform && (
            <>
              <Link className="btn btn-small" to={`/edges/${encodeURIComponent(edge.id)}/objects`}>
                BACnet
              </Link>
              <Link className="btn btn-small" to={`/edges/${encodeURIComponent(edge.id)}/mbus`}>
                M-Bus
              </Link>
              <Link className="btn btn-small" to={`/edges/${encodeURIComponent(edge.id)}/knx`}>
                KNX
              </Link>
              <Link className="btn btn-small" to={`/edges/${encodeURIComponent(edge.id)}/firewall`}>
                Firewall
              </Link>
              <Link className="btn btn-small" to={`/edges/${encodeURIComponent(edge.id)}/update`}>
                Update
              </Link>
              <EdgeLifecycleActions edge={edge} onPanel={onPanel} onChanged={onChanged} />
            </>
          )}
        </div>
      </td>
      <td>
        <StatusBadge status={edge.status} />
      </td>
      {platform && (
        <td>
          <EnrollmentCell edge={edge} now={now} timeZone={timeZone} />
        </td>
      )}
      {showVpn && (
        <td>{edge.vpn ? <VpnCell vpn={edge.vpn} now={now} timeZone={timeZone} /> : null}</td>
      )}
      <td>
        <Age ts={edge.last_seen} now={now} timeZone={timeZone} />
      </td>
      <td className="mono">
        {h.agentVersion ?? PLACEHOLDER}
        {edge.config_version !== null && (
          <div className="muted small">config v{edge.config_version}</div>
        )}
        {h.rulesVersion !== null && <div className="muted small">rules v{h.rulesVersion}</div>}
      </td>
      <td className="num">{n(h.uptimeS, formatDuration)}</td>
      <td className="num">
        {n(h.bufferDepth)}
        {h.bufferOldestAgeS !== null && h.bufferOldestAgeS > 0 && (
          <div className={`small ${h.bufferOldestAgeS > 300 ? 'warn' : 'muted'}`}>
            oldest {formatDuration(h.bufferOldestAgeS)}
          </div>
        )}
      </td>
      <td className={`num ${h.bufferDropped ? 'bad' : ''}`}>{n(h.bufferDropped)}</td>
      <td className="num">{n(h.cpuPercent, (v) => `${formatFloat(v)} %`)}</td>
      <td className="num">{n(h.memoryMb, formatMegabytes)}</td>
      <td className="num">{n(h.diskFreeMb, formatMegabytes)}</td>
      <td className={`num ${devicesWarn ? 'warn' : ''}`}>
        {h.bacnetDevices === null
          ? PLACEHOLDER
          : `${n(h.bacnetDevicesOnline)} / ${n(h.bacnetDevices)}`}
        {h.bacnetState && <div className="muted small">{h.bacnetState}</div>}
      </td>
      <td className="num">
        {n(h.bacnetTimeouts)} /{' '}
        <span className={h.bacnetErrors ? 'bad' : undefined}>{n(h.bacnetErrors)}</span>
      </td>
    </tr>
  );
}

export function SitePage() {
  const { siteId = '' } = useParams();
  const api = useApi();
  const now = useNow(1000);
  const sites = useResource('sites', () => api.sites());
  const edges = useResource(`edges:${siteId}`, () => api.edges(siteId), EDGE_REFRESH_MS);
  const site = sites.data?.find((s) => s.id === siteId);
  const tz = site?.timezone ?? undefined;
  const updated = edges.data ? now : null;
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const platform = isPlatform(user);
  const [adding, setAdding] = useState(false);
  const [panel, setPanel] = useState<EdgePanel | null>(
    () => (location.state as SitePageState | null)?.panel ?? null,
  );
  // The install token is shown once: drop it from the history entry.
  useEffect(() => {
    if ((location.state as SitePageState | null)?.panel) {
      void navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);
  // The virtual LoRaWAN unit (WP9) has its own page.
  const realEdges = edges.data?.filter((e) => e.kind !== 'lorawan');
  const lorawan = edges.data?.find((e) => e.kind === 'lorawan');
  const showVpn = realEdges?.some((e) => e.vpn != null) ?? false;
  const panelEdge = (id: string) => edges.data?.find((e) => e.id === id)?.edge_node_id ?? id;

  return (
    <section>
      <div className="breadcrumbs">
        <Link to="/sites">Sites</Link> <span aria-hidden="true">/</span>
      </div>
      <div className="page-head">
        <div>
          <h1>{site?.name ?? (sites.loading ? 'Site' : 'Unknown site')}</h1>
          {site && (
            <div className="muted small">
              {site.tenant.name} · group <code>{site.sparkplug_group_id}</code> · {site.timezone}
            </div>
          )}
        </div>
        <div className="page-actions">
          {platform && (
            <button
              type="button"
              className="btn"
              onClick={() => {
                setAdding(true);
              }}
              disabled={adding || !site}
            >
              Add edge
            </button>
          )}
          <Link className="btn" to={`/points?site_id=${encodeURIComponent(siteId)}`}>
            Points{site ? ` (${formatCount(site.points)})` : ''}
          </Link>
          <Link className="btn" to={`/sites/${encodeURIComponent(siteId)}/lorawan`}>
            LoRaWAN{lorawan ? ` (${formatCount(lorawan.devices)})` : ''}
          </Link>
          {site && canOperateSite(user, site) && (
            <Link className="btn" to={`/sites/${encodeURIComponent(siteId)}/rules`}>
              Rules
            </Link>
          )}
        </div>
      </div>

      {adding && site && (
        <AddEdgeForm
          sites={[site]}
          siteId={site.id}
          onCancel={() => {
            setAdding(false);
          }}
          onCreated={(created, token, tokenError) => {
            setAdding(false);
            if (token) {
              setPanel({ kind: 'install', edgeNodeId: created.edge_node_id, token, created: true });
            } else if (tokenError) {
              window.alert(`Edge created, but no install command: ${tokenError.message}`);
            }
            edges.reload();
            sites.reload();
          }}
        />
      )}
      {panel?.kind === 'install' && (
        <InstallPanel
          edgeNodeId={panel.edgeNodeId}
          token={panel.token}
          created={panel.created}
          onClose={() => {
            setPanel(null);
          }}
        />
      )}
      {panel?.kind === 'vpn' && (
        <VpnKeyPanel
          edgeNodeId={panel.edgeNodeId}
          vpnKey={panel.key}
          onClose={() => {
            setPanel(null);
          }}
        />
      )}
      {panel?.kind === 'result' && (
        <LifecycleResultPanel
          panel={panel}
          edgeNodeId={panel.result.edge_node_id || panelEdge(panel.edgeId)}
          onRetry={() => {
            const retry =
              panel.action === 'disable'
                ? api.disableEdge(panel.edgeId)
                : api.enableEdge(panel.edgeId);
            void retry.then(
              (result) => {
                setPanel({ ...panel, result });
                edges.reload();
              },
              (err: unknown) => {
                window.alert(err instanceof Error ? err.message : String(err));
              },
            );
          }}
          onClose={() => {
            setPanel(null);
          }}
        />
      )}

      <div className="section-head">
        <h2>Edge units</h2>
        <span className="muted small" title={updated ? formatTimestamp(updated, tz) : undefined}>
          {edges.loading && edges.data
            ? 'Refreshing…'
            : `Refreshes every ${EDGE_REFRESH_MS / 1000} s`}
        </span>
      </div>
      {edges.error && <ErrorBox error={edges.error} onRetry={edges.reload} />}
      {!edges.data && edges.loading && <Spinner />}
      {realEdges?.length === 0 && <p className="empty">No edge units at this site.</p>}
      {realEdges && realEdges.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Edge</th>
                <th>Status</th>
                {platform && <th>Enrollment</th>}
                {showVpn && <th>VPN</th>}
                <th>Last seen</th>
                <th>Agent</th>
                <th className="num">Uptime</th>
                <th className="num" title="Samples buffered on the edge">
                  Buffer
                </th>
                <th className="num" title="Samples dropped from the buffer">
                  Dropped
                </th>
                <th className="num">CPU</th>
                <th className="num">Memory</th>
                <th className="num">Disk free</th>
                <th className="num" title="BACnet devices online / configured">
                  BACnet devices
                </th>
                <th className="num" title="BACnet timeouts / errors">
                  Timeouts / errors
                </th>
              </tr>
            </thead>
            <tbody>
              {realEdges.map((e) => (
                <EdgeRow
                  key={e.id}
                  edge={e}
                  siteId={siteId}
                  now={now}
                  timeZone={tz ?? 'Europe/Bratislava'}
                  showVpn={showVpn}
                  platform={platform}
                  onPanel={setPanel}
                  onChanged={edges.reload}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
